import { movies } from "@/mocks/movies";
import { tvShows } from "@/mocks/tvShows";
import { varietyShows } from "@/mocks/varietyShows";
import type { Video, VideoType, Region } from "@/types/video";

export const allVideos: Video[] = [...movies, ...tvShows, ...varietyShows];

export interface TypeMeta {
  key: VideoType;
  label: string;
  title: string;
  slogan: string;
  description: string;
  icon: string;
}

export const TYPE_META: Record<VideoType, TypeMeta> = {
  movie: {
    key: "movie",
    label: "电影",
    title: "电影频道",
    slogan: "光影之间，遇见好故事",
    description: "汇集科幻、悬疑、爱情、剧情等多元题材的电影作品，每周持续更新精选片单。",
    icon: "ri-film-line",
  },
  tv: {
    key: "tv",
    label: "电视剧",
    title: "电视剧频道",
    slogan: "好剧连连，追个不停",
    description: "覆盖古装、都市、悬疑、年代等热门剧集，同步更新最新进度，追剧不迷路。",
    icon: "ri-tv-2-line",
  },
  variety: {
    key: "variety",
    label: "综艺",
    title: "综艺频道",
    slogan: "轻松一下，快乐加满",
    description: "音乐、美食、户外、推理等多样综艺节目，为你的休闲时光增添轻松与欢笑。",
    icon: "ri-mic-line",
  },
};

export const TYPE_ORDER: VideoType[] = ["movie", "tv", "variety"];

export const REGIONS: Array<Region | "不限"> = ["不限", "大陆", "港台", "欧美", "日韩"];

export const CURRENT_YEAR = 2024;

export const YEARS: number[] = [2024, 2023, 2022];

export type SortKey = "hot" | "updated" | "alpha";

export const SORT_OPTIONS: Array<{ key: SortKey; label: string }> = [
  { key: "hot", label: "按热度" },
  { key: "updated", label: "按更新时间" },
  { key: "alpha", label: "按字母顺序" },
];

export function isVideoType(value: string | undefined): value is VideoType {
  return value === "movie" || value === "tv" || value === "variety";
}

export function getVideoById(id: string | undefined): Video | null {
  if (!id) return null;
  return allVideos.find((item) => item.id === id) ?? null;
}

export function getVideosByType(type: VideoType): Video[] {
  return allVideos.filter((item) => item.type === type);
}

export function sortVideos(list: Video[], sort: SortKey): Video[] {
  const copy = [...list];
  if (sort === "hot") return copy.sort((a, b) => b.hot - a.hot);
  if (sort === "updated") return copy.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  return copy.sort((a, b) => a.title.localeCompare(b.title, "zh-Hans-CN"));
}

export function getHotVideos(limit = 20): Video[] {
  return sortVideos(allVideos, "hot").slice(0, limit);
}

export function getLatestVideos(limit = 25): Video[] {
  return sortVideos(allVideos, "updated").slice(0, limit);
}

export function getFeaturedVideos(limit = 5): Video[] {
  const featured = allVideos.filter((item) => item.featured);
  if (featured.length >= limit) return featured.slice(0, limit);
  const fillers = sortVideos(allVideos, "hot").filter((item) => !item.featured);
  return [...featured, ...fillers].slice(0, limit);
}

export interface FilterOptions {
  type?: VideoType;
  region?: Region | "不限";
  year?: number | "不限";
  sort?: SortKey;
  keyword?: string;
}

export function filterVideos(options: FilterOptions): Video[] {
  const { type, region, year, sort = "hot", keyword } = options;
  let list = allVideos;

  if (type) list = list.filter((item) => item.type === type);
  if (region && region !== "不限") list = list.filter((item) => item.region === region);
  if (year && year !== "不限") list = list.filter((item) => item.year === year);

  const trimmed = keyword?.trim();
  if (trimmed) {
    const lower = trimmed.toLowerCase();
    list = list.filter(
      (item) =>
        item.title.toLowerCase().includes(lower) ||
        item.actors.some((actor) => actor.toLowerCase().includes(lower)),
    );
  }

  return sortVideos(list, sort);
}

export function searchVideos(keyword: string, type?: VideoType): Video[] {
  return filterVideos({ keyword, type, sort: "hot" });
}

export function getRelatedVideos(video: Video, limit = 5): Video[] {
  const sameType = allVideos.filter((item) => item.id !== video.id && item.type === video.type);
  const scoreOf = (item: Video) =>
    item.genres.filter((genre) => video.genres.includes(genre)).length;
  const sorted = sameType.sort((a, b) => scoreOf(b) - scoreOf(a) || b.hot - a.hot);
  return sorted.slice(0, limit);
}

export function splitIntoPages<T>(list: T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return list.slice(start, start + pageSize);
}

export function totalPages(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

export function regionLabelOf(video: Video): string {
  return video.region;
}